<template>
  <div class="mfa-disable">
    <v-dialog
      v-model="dialog"
      max-width="600px"
      persistent
      :fullscreen="$vuetify.breakpoint.xs"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn color="error" outlined text v-bind="attrs" v-on="on">
          <v-icon left>mdi-shield-off</v-icon>
          Deaktivieren
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <span class="text-h5"
            >Zwei-Faktor-Authentifizierung deaktivieren</span
          >
        </v-card-title>

        <v-card-text>
          <v-alert type="error" text class="mb-4">
            Das Deaktivieren der Zwei-Faktor-Authentifizierung macht Dein Konto
            weniger sicher.
          </v-alert>

          <v-stepper v-model="step" vertical flat>
            <!-- Step 1: Password -->
            <v-stepper-step :complete="step > 1" step="1">
              Identität bestätigen
            </v-stepper-step>
            <v-stepper-content step="1">
              <p class="mb-2">Bitte bestätige zuerst Deine Identität:</p>
              <v-text-field
                v-model="password"
                label="Passwort"
                type="password"
                outlined
                dense
                @keyup.enter="disableMFA"
              ></v-text-field>
              <v-alert v-if="errorMsg" type="error" class="mb-4" text>
                {{ errorMsg }}
              </v-alert>
              <v-btn
                color="primary"
                depressed
                @click="disableMFA"
                :loading="loading"
                :disabled="!password"
              >
                Weiter
              </v-btn>
            </v-stepper-content>

            <!-- Step 2: TOTP Code -->
            <v-stepper-step :complete="step > 2" step="2">
              Bestätigungscode eingeben
            </v-stepper-step>
            <v-stepper-content step="2">
              <p class="mb-4">
                Gib den 6-stelligen Code aus Deiner Authenticator-App ein:
              </p>
              <v-text-field
                v-model="totpCode"
                label="Bestätigungscode"
                outlined
                dense
                autofocus
                maxlength="6"
                @keyup.enter="completeMFAAndDisable"
              ></v-text-field>
              <v-alert v-if="errorMsg" type="error" class="mb-4" text>
                {{ errorMsg }}
              </v-alert>
              <v-btn
                color="primary"
                depressed
                @click="completeMFAAndDisable"
                :loading="loading"
                :disabled="!totpCode || totpCode.length !== 6"
              >
                Deaktivieren
              </v-btn>
            </v-stepper-content>
          </v-stepper>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Abbrechen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as fb from "@/firebase";
import { TotpMultiFactorGenerator } from "firebase/auth";
import { handleAuthError } from "@/util/firebase/FirebaseErrorHandling.js";

export default {
  name: "MFADisable",
  data() {
    return {
      dialog: false,
      loading: false,
      step: 1,
      password: "",
      totpCode: "",
      mfaResolver: null,
      errorMsg: "",
    };
  },
  methods: {
    async disableMFA() {
      this.loading = true;
      this.errorMsg = "";
      try {
        const user = fb.auth.currentUser;

        // Re-authenticate before sensitive MFA operation
        const credential = fb.EmailAuthProvider.credential(
          user.email,
          this.password,
        );
        await user.reauthenticateWithCredential(credential);

        // No MFA challenge needed – unenroll directly
        await this.unenrollAllFactors();
      } catch (error) {
        if (error.code === "auth/multi-factor-auth-required") {
          // MFA challenge required as part of re-authentication
          this.mfaResolver = error.resolver;
          this.step = 2;
        } else {
          this.errorMsg = handleAuthError(error.code) || error.message;
          console.error("Error disabling MFA:", error);
        }
      } finally {
        this.loading = false;
      }
    },

    async completeMFAAndDisable() {
      this.loading = true;
      this.errorMsg = "";
      try {
        const selectedHint = this.mfaResolver.hints[0];
        const multiFactorAssertion =
          TotpMultiFactorGenerator.assertionForSignIn(
            selectedHint.uid,
            this.totpCode,
          );
        await this.mfaResolver.resolveSignIn(multiFactorAssertion);

        // Re-authentication complete – now unenroll all factors
        await this.unenrollAllFactors();
      } catch (error) {
        this.errorMsg =
          handleAuthError(error.code) ||
          error.message ||
          "Ungültiger Code. Bitte versuche es erneut.";
        console.error("Error completing MFA challenge:", error);
      } finally {
        this.loading = false;
      }
    },

    async unenrollAllFactors() {
      const user = fb.auth.currentUser;
      const enrolledFactors = user.multiFactor.enrolledFactors;

      for (const factor of enrolledFactors) {
        await user.multiFactor.unenroll(factor);
      }

      this.$emit("mfa-disabled");
      this.closeDialog();
      this.$emit("refresh");
    },

    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },

    resetForm() {
      this.step = 1;
      this.password = "";
      this.totpCode = "";
      this.mfaResolver = null;
      this.errorMsg = "";
    },
  },
};
</script>
